import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTitle } from "../hooks/useTitle";
import { Rating, ProductDetailSkeleton, ReviewList, ReviewForm, ReviewListSkeleton } from "../components";
import { ProductQRCode } from "../components/ProductQRCode";
import { useCart } from "../context";
import { useProduct } from "../hooks/useProducts";
import { useUserOrders } from "../hooks/useUser";
import { useReviewsByProduct, useCreateReview, useUpdateReview, useDeleteReview } from "../hooks/useReviews";
import { getProductImageUrl, getProductImageKey } from "../utils/productImage";
import { Card } from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

export const ProductDetail = () => {
  const { cartList, addToCart, removeFromCart } = useCart();
  const [inCart, setInCart] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState(null);
  const { id } = useParams();

  // Get current user ID
  const currentUserId =
    typeof window !== "undefined"
      ? JSON.parse(sessionStorage.getItem("cbid") || "null")
      : null;

  // Use React Query hooks - automatically handles caching, deduplication, and loading states
  const {
    data: product = {},
    isLoading: loading,
    error,
  } = useProduct(id, !!id);

  // Fetch reviews for this product
  const {
    data: reviewsData = { reviews: [], ratingStats: { averageRating: 0, reviewCount: 0 } },
    isLoading: reviewsLoading,
  } = useReviewsByProduct(id, !!id);

  // Fetch user orders to check if they can review
  const { data: userOrders = [] } = useUserOrders();

  // Mutations
  const createReviewMutation = useCreateReview();
  const updateReviewMutation = useUpdateReview();
  const deleteReviewMutation = useDeleteReview();

  useTitle(product.name);

  // Check if user has ordered this product
  const hasOrderedProduct = useMemo(() => {
    if (!userOrders || userOrders.length === 0) return false;
    return userOrders.some((order) =>
      order.cartList?.some((item) => item.id === id)
    );
  }, [userOrders, id]);

  // Get order ID for review (first order containing this product)
  const orderIdForReview = useMemo(() => {
    if (!hasOrderedProduct) return null;
    const order = userOrders.find((order) =>
      order.cartList?.some((item) => item.id === id)
    );
    return order?.id || null;
  }, [hasOrderedProduct, userOrders, id]);

  // Check if user has already reviewed this product
  const userReview = useMemo(() => {
    if (!currentUserId || !reviewsData.reviews) return null;
    return reviewsData.reviews.find((review) => review.userId === currentUserId) || null;
  }, [currentUserId, reviewsData.reviews]);

  // Use review stats if available, otherwise fall back to product rating
  const displayRating = reviewsData.ratingStats?.averageRating || product.rating || 0;
  const reviewCount = reviewsData.ratingStats?.reviewCount || 0;

  /**
   * Generate product URL for QR code
   * Uses REACT_APP_BASE_URL for production (Vercel), falls back to window.location.origin for localhost
   * This ensures QR codes work in all environments (localhost, staging, production)
   * 
   * Memoized to prevent unnecessary recalculations
   */
  const productUrl = useMemo(() => {
    // Priority: 1. REACT_APP_BASE_URL (set in Vercel env vars), 2. window.location.origin (localhost)
    const baseUrl = process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "");
    return baseUrl ? `${baseUrl}/products/${id}` : null;
  }, [id]);

  // Show error toast if API call fails
  useEffect(() => {
    if (error) {
      toast.error(error.message, {
        closeButton: true,
        position: "bottom-right",
      });
    }
  }, [error]);

  useEffect(() => {
    const productInCart = cartList.find((item) => item.id === product.id);

    if (productInCart) {
      setInCart(true);
    } else {
      setInCart(false);
    }
  }, [cartList, product.id]);

  // Handle review form submission
  const handleReviewSubmit = useCallback(
    (reviewData) => {
      if (editingReview) {
        // Update existing review
        updateReviewMutation.mutate(
          {
            reviewId: editingReview.id,
            updates: reviewData,
            productId: editingReview.productId || id, // Pass productId for cache invalidation
          },
          {
            onSuccess: () => {
              setEditingReview(null);
              setShowReviewForm(false);
            },
          }
        );
      } else {
        // Create new review
        createReviewMutation.mutate(
          {
            productId: id,
            orderId: orderIdForReview,
            ...reviewData,
          },
          {
            onSuccess: () => {
              setShowReviewForm(false);
            },
          }
        );
      }
    },
    [editingReview, id, orderIdForReview, createReviewMutation, updateReviewMutation]
  );

  // Handle review edit
  const handleReviewEdit = useCallback((review) => {
    setEditingReview(review);
    setShowReviewForm(true);
  }, []);

  // Handle review delete (opens confirmation dialog)
  const handleReviewDelete = useCallback((review) => {
    setReviewToDelete(review);
    setDeleteDialogOpen(true);
  }, []);

  // Handle delete confirmation
  const handleDeleteConfirm = useCallback(() => {
    if (reviewToDelete) {
      deleteReviewMutation.mutate(reviewToDelete.id, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setReviewToDelete(null);
        },
      });
    }
  }, [reviewToDelete, deleteReviewMutation]);

  // Handle cancel review form
  const handleCancelReview = useCallback(() => {
    setShowReviewForm(false);
    setEditingReview(null);
  }, []);

  if (loading) {
    return <ProductDetailSkeleton />;
  }

  return (
    <main>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {/* Title and Overview */}
        <div className="text-center mb-4">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-slate-200">
            {product.name}
          </h1>
          <p className="mb-2 text-base sm:text-lg text-center text-gray-700 dark:text-slate-300 max-w-7xl mx-auto">
            {product.overview}
          </p>
        </div>

        {/* Main Content Card */}
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-sm dark:shadow-slate-900/50 p-4 sm:p-6 lg:p-8">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-8">
            {/* Left Section - Product Image */}
            <div className="flex-shrink-0 w-full lg:w-auto lg:max-w-xl">
              <div className="rounded-lg overflow-hidden border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 h-full">
                <img
                  className="w-full h-full rounded-lg object-cover"
                  src={getProductImageUrl(product) || product.poster}
                  key={getProductImageKey(product)}
                  alt={product.name}
                />
              </div>
            </div>

            {/* Right Section - Product Details Card */}
            <div className="flex-1 min-w-0 flex flex-col">
              {/* Flex Layout: QR Code on top right parallel to price/rating/badges/add button */}
              <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-6 mb-4">
                {/* Product Details (Price, Rating, Badges, Add to Cart) - Wrapped in inner div */}
                <div className="flex-1 min-w-0 space-y-2 sm:space-y-2">
                  {/* Price Badge */}
                  <div>
                    <span className="inline-flex items-center px-4 py-2 rounded-lg text-2xl sm:text-3xl font-bold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800">
                      <i className="bi bi-currency-dollar mr-1.5"></i>
                      {product.price?.toFixed(2) || "0.00"}
                    </span>
                  </div>

                  {/* Rating */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex items-center flex-shrink-0">
                      <Rating rating={displayRating} />
                    </div>
                    <div className="text-sm text-gray-600 dark:text-slate-400 flex flex-col sm:flex-row sm:items-center gap-0 sm:gap-1">
                      <span>({displayRating.toFixed(1)} out of 5)</span>
                      {reviewCount > 0 && (
                        <>
                          <span className="hidden sm:inline"> · </span>
                          <span>{reviewCount} {reviewCount === 1 ? "review" : "reviews"}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Product Badges */}
                  <div className="flex flex-wrap gap-2">
                    {product.best_seller && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                        <i className="bi bi-star-fill mr-1.5 text-xs"></i>
                        Best Seller
                      </span>
                    )}
                    {/* Stock Quantity Badge - Show detailed stock info if available */}
                    {product.stock !== undefined ? (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${
                        product.stock === 0 
                          ? "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800"
                          : product.stock <= (product.lowStockThreshold || 10)
                          ? "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800"
                          : "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800"
                      }`}>
                        <i className={`bi ${product.stock === 0 ? "bi-x-circle" : product.stock <= (product.lowStockThreshold || 10) ? "bi-exclamation-triangle" : "bi-check-circle"} mr-1.5 text-xs`}></i>
                        {product.stock === 0 
                          ? "Out of Stock" 
                          : product.stock <= (product.lowStockThreshold || 10)
                          ? `Low Stock (${product.stock} left)`
                          : `${product.stock} in stock`
                        }
                      </span>
                    ) : (
                      // Fallback to in_stock boolean if stock quantity not available
                      product.in_stock ? (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                          <i className="bi bi-check-circle mr-1.5 text-xs"></i>
                          In Stock
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                          <i className="bi bi-x-circle mr-1.5 text-xs"></i>
                          Out of Stock
                        </span>
                      )
                    )}
                    {product.size && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                        <i className="bi bi-file-earmark mr-1.5 text-xs"></i>
                        {product.size} MB
                      </span>
                    )}
                  </div>

                  {/* Add to Cart Button */}
                  <div className="py-2">
                    {!inCart ? (
                      <button
                        onClick={() => {
                          // Prevent adding out-of-stock items
                          if (!product.in_stock) {
                            return;
                          }
                          addToCart(product);
                        }}
                        disabled={!product.in_stock}
                        className={`inline-flex items-center justify-center py-3 px-6 text-base sm:text-lg font-medium text-center text-white rounded-lg transition-colors ${
                          product.in_stock
                            ? "bg-blue-700 hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-700"
                            : "bg-gray-400 cursor-not-allowed"
                        }`}
                      >
                        <i className="bi bi-cart-plus mr-2"></i>
                        Add To Cart
                      </button>
                    ) : (
                      <button
                        onClick={() => removeFromCart(product)}
                        className={`inline-flex items-center justify-center py-3 px-6 text-base sm:text-lg font-medium text-center text-white rounded-lg transition-colors ${
                          product.in_stock
                            ? "bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
                            : "bg-gray-400 cursor-not-allowed"
                        }`}
                        disabled={!product.in_stock}
                      >
                        <i className="bi bi-trash3 mr-2"></i>
                        Remove From Cart
                      </button>
                    )}
                  </div>
                </div>

                {/* QR Code Card - Top Right (beside product details) */}
                {(product.qrCode || productUrl) && (
                  <div className="flex-shrink-0 w-full lg:w-auto lg:max-w-[200px]">
                    <ProductQRCode
                      qrCode={product.qrCode}
                      productUrl={productUrl}
                      productName={product.name}
                      productId={product.id}
                      className="w-full"
                    />
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-200 dark:border-slate-700 pt-1 sm:pt-1 mt-4"></div>

              {/* Product Description */}
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-slate-200 mb-2">
                  Description
                </h2>
                <p className="text-base sm:text-lg text-gray-700 dark:text-slate-300 leading-relaxed whitespace-pre-line text-justify">
                  {product.long_description}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-8">
          <Card className="p-4 sm:p-6 lg:p-8">
            {/* Review Form (if user has ordered and not already reviewed, or editing) */}
            {currentUserId && hasOrderedProduct && !userReview && !showReviewForm && (
              <div className="mb-6">
                <button
                  onClick={() => setShowReviewForm(true)}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white rounded-lg font-medium transition-colors"
                >
                  <i className="bi bi-star mr-2"></i>
                  Write a Review
                </button>
              </div>
            )}

            {(showReviewForm || editingReview) && (
              <div className="mb-6 pb-6 border-b border-gray-200 dark:border-slate-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-200 mb-4">
                  {editingReview ? "Edit Your Review" : "Write a Review"}
                </h3>
                <ReviewForm
                  onSubmit={handleReviewSubmit}
                  onCancel={handleCancelReview}
                  initialData={editingReview}
                  isSubmitting={
                    createReviewMutation.isPending || updateReviewMutation.isPending
                  }
                />
              </div>
            )}

            {/* Reviews List */}
            {reviewsLoading ? (
              <ReviewListSkeleton />
            ) : (
              <ReviewList
                reviews={reviewsData.reviews || []}
                isLoading={reviewsLoading}
                onEdit={handleReviewEdit}
                onDelete={handleReviewDelete}
                currentUserId={currentUserId}
              />
            )}
          </Card>
        </div>

        {/* Delete Review Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Review</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this review? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setReviewToDelete(null)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={deleteReviewMutation.isPending}
                className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800"
              >
                {deleteReviewMutation.isPending ? "Deleting..." : "Delete"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </section>
    </main>
  );
};
